import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Chart, useChart } from 'src/components/chart';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { formatInstant } from '../vaults/helpers';
import { useVaults } from '../vaults/useVaults';
import {
  DASHBOARD_NS as NS,
  DASHBOARD_PERMISSION_RESOURCE as RESOURCE,
  SERIES_DAYS,
} from './constants';
import {
  EMPTY,
  axisLabel,
  feeTooltip,
  feesLine,
  holdersLine,
  keepSelection,
  navLines,
  navTooltip,
  statCards,
  topVaultIds,
} from './helpers';
import { useDashboard } from './useDashboard';

interface TileProps {
  label: string;
  value: string;
  caption?: string;
  note?: string;
  tone?: 'positive' | 'negative';
  testId: string;
}

const Tile = ({ label, value, caption, note, tone, testId }: TileProps) => (
  <Card data-testid={testId} sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{ mt: 1, color: tone === 'negative' ? 'error.main' : undefined }}
        data-testid={`${testId}-value`}
      >
        {value}
      </Typography>
      {note && (
        <Typography variant="caption" color="warning.main" data-testid={`${testId}-note`}>
          {note}
        </Typography>
      )}
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/** One chart card: a loading panel, the banner for a failed read, or the chart. */
const ChartCard = ({
  title,
  subheader,
  loading,
  error,
  children,
}: {
  title: string;
  subheader?: string;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}) => (
  <Card>
    <CardHeader title={title} subheader={subheader} />
    <CardContent>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Stack sx={{ alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

/**
 * §14.2 Dashboard (XBIT-4246): the four cards and three charts over persisted rows, for the viewer's own zone. A figure
 * the engine could not give shows "—", never 0; a day without a cut-off is a gap in the line, never a drop to 0.
 */
const DashboardPage = () => {
  const translate = useTranslate();
  const { stats, series, statsError, seriesError, loading, refetch } = useDashboard();
  const { vaults, refetch: refetchVaults } = useVaults();
  const cards = statCards(stats);
  const dates = useMemo(() => (series ? series.days.map((d) => axisLabel(d.date)) : []), [series]);

  // The NAV chart's vaults: the top ten by TVL until the viewer picks others.
  const defaults = useMemo(() => (series ? topVaultIds(series, vaults) : []), [series, vaults]);
  const [picked, setPicked] = useState<string[] | null>(null);
  useEffect(() => setPicked((current) => keepSelection(current, series)), [series]);
  const chosen = picked ?? defaults;

  const navOptions = useChart({
    xaxis: { categories: dates },
    stroke: { width: 2, curve: 'straight' },
    markers: { size: 3 },
    legend: { show: true },
    yaxis: { labels: { formatter: (v: number) => navTooltip(v) } },
    tooltip: { y: { formatter: (v: number | null) => navTooltip(v) } },
  });
  const holdersOptions = useChart({
    xaxis: { categories: dates },
    stroke: { width: 2, curve: 'straight' },
    markers: { size: 3 },
    yaxis: { labels: { formatter: (v: number) => String(Math.round(v)) } },
    tooltip: { y: { formatter: (v: number | null) => (v === null ? EMPTY : String(v)) } },
  });
  const feesOptions = useChart({
    xaxis: { categories: dates },
    tooltip: {
      y: {
        formatter: (_: number | null, opts?: { dataPointIndex?: number }) =>
          feeTooltip(series, opts?.dataPointIndex),
      },
    },
  });

  const refresh = () => {
    refetch();
    refetchVaults();
  };

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={refresh}
              disabled={loading}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Stack spacing={3} sx={{ mt: 3 }}>
          {statsError && <Alert severity="error">{statsError}</Alert>}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Tile
                testId="tile-tvl"
                label={translate(`${NS}.tile.tvl`)}
                value={cards.tvl}
                note={
                  cards.unvalued > 0
                    ? translate(`${NS}.tile.unvalued`, { count: cards.unvalued })
                    : undefined
                }
                caption={
                  cards.valuedAt
                    ? translate(`${NS}.tile.valuedAt`, { at: formatInstant(cards.valuedAt) })
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile
                testId="tile-holders"
                label={translate(`${NS}.tile.holders`)}
                value={cards.holders}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile
                testId="tile-net"
                label={translate(`${NS}.tile.netSubscriptions`)}
                value={cards.netSubscriptions}
                tone={cards.netSign < 0 ? 'negative' : undefined}
                caption={
                  stats
                    ? translate(`${NS}.tile.today`, { day: stats.day, zone: stats.timeZone })
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile
                testId="tile-fees"
                label={translate(`${NS}.tile.managerFees`)}
                value={cards.managerFees}
                caption={
                  stats
                    ? translate(`${NS}.tile.today`, { day: stats.day, zone: stats.timeZone })
                    : undefined
                }
              />
            </Grid>
          </Grid>

          <ChartCard
            title={translate(`${NS}.chart.nav`)}
            subheader={translate(`${NS}.chart.days`, { days: SERIES_DAYS })}
            loading={loading && !series}
            error={seriesError}
          >
            {series && (
              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  size="small"
                  options={series.vaultNav.map((v) => v.vaultId)}
                  value={chosen}
                  onChange={(_, next) => setPicked(next)}
                  getOptionLabel={(id) => series.vaultNav.find((v) => v.vaultId === id)?.name ?? id}
                  renderTags={(ids, getTagProps) =>
                    ids.map((id, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={id}
                        size="small"
                        label={series.vaultNav.find((v) => v.vaultId === id)?.name ?? id}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={translate(`${NS}.chart.vaults`)} />
                  )}
                />
                <Chart
                  type="line"
                  series={navLines(series, chosen)}
                  options={navOptions}
                  height={320}
                />
              </Stack>
            )}
          </ChartCard>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ChartCard
                title={translate(`${NS}.chart.holders`)}
                subheader={`${translate(`${NS}.chart.holdersBasis`)} · ${translate(`${NS}.chart.days`, { days: SERIES_DAYS })}`}
                loading={loading && !series}
                error={seriesError}
              >
                {series && (
                  <Chart
                    type="line"
                    series={holdersLine(series).map((s) => ({
                      ...s,
                      name: `${translate(`${NS}.chart.holders`)} · ${translate(`${NS}.chart.holdersBasis`)}`,
                    }))}
                    options={holdersOptions}
                    height={300}
                  />
                )}
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard
                title={translate(`${NS}.chart.fees`)}
                subheader={translate(`${NS}.chart.days`, { days: SERIES_DAYS })}
                loading={loading && !series}
                error={seriesError}
              >
                {series && (
                  <Chart
                    type="bar"
                    series={feesLine(series).map((s) => ({
                      ...s,
                      name: translate(`${NS}.chart.fees`),
                    }))}
                    options={feesOptions}
                    height={300}
                  />
                )}
              </ChartCard>
            </Grid>
          </Grid>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default DashboardPage;
